var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { requireToken } from '../../utility/general';
import { fetchServiceBayBoard, createServiceBay, renameServiceBay, deleteServiceBay, unassignInvoiceFromBay, } from '../../http/service-bays';
import Card from '../components/card';
import Button from '../components/button';
import IconButton from '../components/button.icon';
import Icon from '../components/icon';
import CInput from '../components/input';
const bayHeaderClass = (position) => {
    const colors = ['bg-primary-subtle text-primary', 'bg-success-subtle text-success', 'bg-warning-subtle text-warning-emphasis', 'bg-info-subtle text-info-emphasis'];
    return colors[(position - 1) % colors.length];
};
const MAX_BAYS = 12;
export default (_) => {
    const token = requireToken();
    const merchantId = token.merchant.id;
    const [board, setBoard] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [editingBayId, setEditingBayId] = React.useState(null);
    const [editingName, setEditingName] = React.useState('');
    const [creating, setCreating] = React.useState(false);
    const refresh = React.useCallback(() => {
        setLoading(true);
        fetchServiceBayBoard(merchantId)
            .then((b) => { setBoard(b); setLoading(false); })
            .catch(() => { setLoading(false); setError('Failed to load service bays.'); });
    }, [merchantId]);
    React.useEffect(() => { refresh(); }, [refresh]);
    const assignmentsByBay = React.useMemo(() => {
        const map = {};
        if (board) {
            for (const a of board.assignments) {
                if (!(a.bay_id in map))
                    map[a.bay_id] = [];
                map[a.bay_id].push(a);
            }
        }
        return map;
    }, [board]);
    const handleAddBay = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!board || creating || board.bays.length >= MAX_BAYS)
            return;
        setCreating(true);
        setError(null);
        try {
            yield createServiceBay(merchantId, null);
            refresh();
        }
        catch (_a) {
            setError('Failed to create bay.');
        }
        finally {
            setCreating(false);
        }
    });
    const startRename = (bay) => {
        setEditingBayId(bay.id);
        setEditingName(bay.name);
    };
    const cancelRename = () => {
        setEditingBayId(null);
        setEditingName('');
    };
    const saveRename = (bay) => __awaiter(void 0, void 0, void 0, function* () {
        const trimmed = editingName.trim();
        if (!trimmed || trimmed === bay.name) {
            cancelRename();
            return;
        }
        setError(null);
        try {
            yield renameServiceBay(merchantId, bay.id, trimmed);
            cancelRename();
            refresh();
        }
        catch (_a) {
            setError(`Failed to rename ${bay.name}.`);
        }
    });
    const handleUnassign = (a) => __awaiter(void 0, void 0, void 0, function* () {
        setError(null);
        try {
            yield unassignInvoiceFromBay(merchantId, a.invoice_id);
            refresh();
        }
        catch (_a) {
            setError(`Failed to unassign invoice ${a.invoice_number}.`);
        }
    });
    const handleDelete = (bay) => __awaiter(void 0, void 0, void 0, function* () {
        const bayAssignments = assignmentsByBay[bay.id] || [];
        if (bayAssignments.length > 0) {
            setError(`${bay.name} has ${bayAssignments.length} invoice${bayAssignments.length === 1 ? '' : 's'} assigned. Reassign or remove them first.`);
            return;
        }
        setError(null);
        try {
            yield deleteServiceBay(merchantId, bay.id);
            refresh();
        }
        catch (_a) {
            setError(`Failed to remove ${bay.name}.`);
        }
    });
    return _jsxs("div", { className: "container-fluid", children: [_jsxs("div", { className: "d-flex align-items-center justify-content-between mb-2", children: [_jsx("div", {}), _jsx("div", { children: _jsxs(Button, { color: "primary", onClick: handleAddBay, disabled: creating || !board || board.bays.length >= MAX_BAYS, children: [_jsx(Icon, { children: "add" }), " Add Bay"] }) })] }), error && _jsx("div", { className: "alert alert-warning", role: "alert", children: error }), loading && _jsx("div", { className: "text-center m-2", children: _jsx("div", { className: "spinner-border text-primary", role: "status", children: _jsx("span", { className: "visually-hidden", children: "Loading..." }) }) }), !loading && board && board.bays.length === 0 &&
                _jsxs(Card, { title: null, className: "text-center py-5", children: [_jsxs("div", { className: "text-muted mb-3", children: [_jsx(Icon, { children: "directions_car" }), _jsx("div", { className: "mt-2", children: "No service bays configured yet." })] }), _jsxs(Button, { color: "primary", onClick: handleAddBay, disabled: creating, children: [_jsx(Icon, { children: "add" }), " Add your first bay"] })] }), !loading && board && board.bays.length > 0 &&
                _jsx("div", { className: "row g-3", children: board.bays.map((bay) => {
                        const bayAssignments = assignmentsByBay[bay.id] || [];
                        const isEditing = editingBayId === bay.id;
                        return _jsx("div", { className: "col-12 col-md-6 col-lg-4 col-xl-3", children: _jsxs(Card, { title: null, className: "h-100", children: [_jsxs("div", { className: `d-flex align-items-center justify-content-between p-2 rounded mb-2 ${bayHeaderClass(bay.position)}`, children: [isEditing
                                                ? _jsx("div", { className: "flex-grow-1 me-2", children: _jsx(CInput, { type: "text", value: editingName, onChange: setEditingName, maxlength: 64, small: true, inline: true, children: "Name" }) })
                                                : _jsx("h5", { className: "mb-0 flex-grow-1", children: bay.name }), _jsx("span", { className: "badge bg-white text-dark me-2", children: bayAssignments.length }), isEditing
                                                ? _jsxs(_Fragment, { children: [_jsx(IconButton, { color: "success", title: "Save", onClick: () => saveRename(bay), children: "check" }), _jsx(IconButton, { color: "secondary", title: "Cancel", onClick: cancelRename, children: "close" })] })
                                                : _jsxs(_Fragment, { children: [_jsx(IconButton, { color: "secondary", title: "Rename", onClick: () => startRename(bay), children: "edit" }), _jsx(IconButton, { color: "danger", title: "Remove bay", onClick: () => handleDelete(bay), children: "delete" })] })] }), _jsx("div", { children: bayAssignments.length === 0
                                            ? _jsxs("div", { className: "text-center text-muted py-4", children: [_jsx(Icon, { children: "directions_car" }), _jsx("div", { className: "small mt-1", children: "No Vehicles" })] })
                                            : bayAssignments.map((a) => _jsxs("div", { className: "border-bottom py-2 d-flex align-items-start justify-content-between", children: [_jsxs("div", { className: "flex-grow-1", children: [_jsx("div", { children: _jsx("a", { href: `#/invoices/${merchantId}/${a.invoice_id}`, className: "fw-bold", children: a.invoice_number }) }), _jsxs("div", { className: "small", children: [a.client_firstname, " ", a.client_lastname] }), !!a.vehicle_description && _jsx("div", { className: "small text-muted", children: a.vehicle_description })] }), _jsx(IconButton, { color: "secondary", title: "Unassign", onClick: () => handleUnassign(a), children: "close" })] }, a.invoice_id)) })] }) }, bay.id);
                    }) })] });
};
